/**
 * La fiche d'un utilisateur : ce qu'il est, et où aller ensuite.
 *
 * ## Deux clics, et le compte
 *
 * Demandé le 20 septembre 2026 : « Quand je clique sur utilisateur, il
 * faudrait une tuile Situation financière de l'utilisateur et ensuite la
 * possibilité en deux clics de voir la situation pour cette famille. »
 *
 * Clic 1 : le nom, depuis la liste des familles, mène ici.
 * Clic 2 : la tuile « Situation financière » mène au compte détaillé.
 *
 * ## Ce que cette page NE lit pas
 *
 * Aucun chiffre financier. `loadFamily()` coûte 39 ms, mais avec un parcours
 * complet de `credit_notes` et de `payments` que le filtre par famille ne
 * traverse pas. Les payer à l'ouverture de chaque fiche, pour des chiffres que
 * l'on ne regarde pas toujours, serait les payer pour rien. Ils se lisent au
 * second clic, quand ils sont demandés.
 *
 * ## Deux champs en lecture seule, et ce n'est pas un oubli
 *
 * Le **rôle** se change par le RPC `admin_change_user_role`, qui fait plus
 * qu'un `update`. L'**abonnement à la lettre d'information** vit dans les
 * fonctions edge `subscribe-newsletter` / `unsubscribe-newsletter` : écrire la
 * colonne seule ferait dire « abonné » à un compte qui ne recevrait rien.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertCircle, ArrowLeft, BadgeCheck, Loader2, Mail, MapPin, Pencil, Phone, Receipt, Wallet,
} from 'lucide-react';
import { lireFicheUtilisateur, type FicheComplete } from '../../lib/utilisateurs/fiche.supabase';
import { nomAffiche } from '../../lib/utilisateurs/fiche';
import { messageErreur } from '../../lib/payments/erreur';
import {
  avecRecherche, CHEMINS, editionUtilisateur, financesDuCompte,
} from '../../lib/navigation/chemins';

export default function AdminUserPage() {
  const { userId = '' } = useParams();
  const [fiche, setFiche] = useState<FicheComplete | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const f = await lireFicheUtilisateur(userId);
      if (!f) setErreur("Aucun compte ne porte cet identifiant.");
      setFiche(f);
    } catch (e) {
      setErreur(messageErreur(e, 'Fiche illisible'));
    } finally {
      setChargement(false);
    }
  }, [userId]);

  useEffect(() => { void charger(); }, [charger]);

  if (chargement) {
    return (
      <p className="flex items-center gap-2 text-sm text-gray-600">
        <Loader2 className="h-4 w-4 animate-spin" /> Lecture de la fiche…
      </p>
    );
  }

  if (erreur || !fiche) {
    return (
      <div className="space-y-4">
        <Retour />
        <p className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {erreur ?? 'Fiche introuvable.'}
        </p>
      </div>
    );
  }

  const { fiche: f, lecture } = fiche;
  const nom = nomAffiche(f);

  return (
    <div className="space-y-6">
      <Retour />

      <div>
        <h1 className="text-2xl font-bold">{nom}</h1>
        <p className="text-gray-600">{f.email}</p>
      </div>

      {/* --- Ce que le compte est ---------------------------------------- */}
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <dl className="grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
          <Champ icone={<Mail className="h-4 w-4" />} libelle="Courriel" valeur={f.email} />
          <Champ icone={<Phone className="h-4 w-4" />} libelle="Téléphone"
                 valeur={[f.telephone, f.telephone2].filter(Boolean).join(' · ')} />
          <Champ icone={<MapPin className="h-4 w-4" />} libelle="Adresse"
                 valeur={[f.adresse, [f.cpostal, f.localite].filter(Boolean).join(' ')]
                   .filter(Boolean).join(', ')} />
          <Champ icone={<BadgeCheck className="h-4 w-4" />} libelle="Type de compte"
                 valeur={f.accountType === 'organisation'
                   ? `Organisation — ${f.organisationName ?? 'sans nom'}`
                   : f.accountType === 'other' ? 'Autre' : 'Parent'} />
          {f.nnational && <Champ libelle="Numéro national" valeur={f.nnational} />}
          {f.companyNumber && <Champ libelle="Numéro d'entreprise" valeur={f.companyNumber} />}
        </dl>

        {/*
          Le rôle et la lettre d'information ne se modifient PAS depuis la
          fiche, et la page le dit plutôt que de les taire : un champ absent se
          lit comme un oubli, un champ expliqué se lit comme une décision.
        */}
        <div className="mt-4 flex flex-wrap gap-3 border-t border-gray-100 pt-3 text-xs text-gray-500">
          <span>
            Rôle : <strong className="text-gray-700">{lecture.role}</strong>
            {' '}— se change dans Utilisateurs (RPC dédié)
          </span>
          <span>
            Lettre d'information :{' '}
            <strong className="text-gray-700">{lecture.newsletter ? 'abonné' : 'non abonné'}</strong>
            {' '}— se change par l'abonnement, pas par la fiche
          </span>
        </div>
      </div>

      {/* --- Où aller ensuite -------------------------------------------- */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <TuileLien
          to={financesDuCompte(userId)}
          icone={<Wallet className="h-5 w-5" />}
          titre="Situation financière"
          detail="Factures, notes de crédit, versements, avoir, et ce qui attend encore en banque."
          ton="indigo"
        />
        <TuileLien
          to={editionUtilisateur(userId)}
          icone={<Pencil className="h-5 w-5" />}
          titre="Modifier les données"
          detail="Coordonnées, adresse, type de compte. Le rôle et l'abonnement se changent ailleurs."
          ton="gris"
        />
        <TuileLien
          to={avecRecherche(CHEMINS.comptabilite.factures, f.email)}
          icone={<Receipt className="h-5 w-5" />}
          titre="Ses factures"
          detail="L'écran des factures, ouvert sur ce compte, où s'émet une note de crédit."
          ton="gris"
        />
      </div>
    </div>
  );
}

function Retour() {
  return (
    <Link to={CHEMINS.utilisateurs.accueil} className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
      <ArrowLeft className="h-4 w-4" /> Utilisateurs
    </Link>
  );
}

function Champ({ icone, libelle, valeur }: { icone?: React.ReactNode; libelle: string; valeur: string }) {
  return (
    <div>
      <dt className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-gray-500">
        {icone}{libelle}
      </dt>
      <dd className="mt-0.5 text-gray-900">{valeur || <span className="text-gray-400">—</span>}</dd>
    </div>
  );
}

function TuileLien({
  to, icone, titre, detail, ton,
}: {
  to: string;
  icone: React.ReactNode;
  titre: string;
  detail: string;
  ton: 'indigo' | 'gris';
}) {
  return (
    <Link
      to={to}
      className={`rounded-xl border p-4 transition-colors ${
        ton === 'indigo'
          ? 'border-indigo-300 bg-indigo-50 hover:bg-indigo-100'
          : 'border-gray-200 bg-white hover:bg-gray-50'
      }`}
    >
      <p className={`flex items-center gap-2 font-medium ${ton === 'indigo' ? 'text-indigo-900' : 'text-gray-800'}`}>
        {icone}{titre}
      </p>
      <p className="mt-1 text-sm text-gray-600">{detail}</p>
    </Link>
  );
}
